import { get } from '@common/Http';
import { get as getCookie } from '@helpers/cookie';
import { getDaysFromMilliseconds } from '@helpers/Date';
import { loadSession, saveSession } from '@helpers/sessionStorage';

const MAX_TIMEOUT = 10000;
const INITIAL_TIMEOUT = 500;
const COOKIE_EXTENSION_SESSION = 'ga.cookie.extension.session';
const COOKIE_EXTENSION_INTERVAL = 20;

const track = (data) => {
    if (typeof data.value !== 'undefined') {
        // eslint-disable-next-line radix
        data.value = parseInt(data.value);
    }

    if (!data.value) {
        data.value = 0;
    }

    // Convert category and action cases to lowercase.
    for (const i in data) {
        if (typeof (data[i]) === 'string') {
            data[i] = data[i].toLowerCase();
        }
    }

    return ga('send', 'event', data.category, data.action, data.label, data.value, { nonInteraction: 1 });
};

/**
 * Track event
 * @param action
 * @param category
 * @param label
 * @param value
 */
export const trackEvent = (action, category, label, value) => track({
    action,
    category,
    label,
    value
});

export const GAInitialisationTimeout = (callback, timeout) => {
    if (timeout > MAX_TIMEOUT) {
        // eslint-disable-next-line no-console
        console.error('GA could not be initialised');

        return;
    }

    setTimeout(() => {
        if (typeof window.ga !== 'undefined') {
            callback();

            return;
        }

        GAInitialisationTimeout(callback, timeout === 0 ? INITIAL_TIMEOUT : timeout * 1.5);
    }, timeout);
};

/**
 * Track promo view
 * @param id
 * @param name
 * @param creative
 * @param position
 */
export const trackPromoView = ({
    id, name, creative, position
}) => {
    const promoData = {
        id,
        name,
        creative,
        position
    };

    GAInitialisationTimeout(() => {
        ga('ec:addPromo', promoData);
        trackEvent('impression', 'Internal Promotions', name, 1);

        if (typeof App.NativeTracking !== 'undefined') {
            App.NativeTracking.sendEcEvent('promo_impression', promoData);
            App.NativeTracking.sendEcEvent('promo_clicked', promoData);
        }
    }, 0);
};

/**
 * Track promo click
 * @param id
 * @param name
 * @param creative
 * @param position
 */
export const trackPromoClick = ({
    id, name, creative, position
}) => {
    const promoData = {
        id,
        name,
        creative,
        position
    };

    ga('ec:addPromo', promoData);
    ga('ec:setAction', 'promo_click');

    if (typeof App.NativeTracking !== 'undefined') {
        App.NativeTracking.sendEcEvent('promo_clicked', promoData);
    }

    trackEvent('click', 'Internal Promotions', name, 1);

    if (typeof App.NativeTracking !== 'undefined') {
        App.NativeTracking.sendEcEvent('promo_clicked', promoData);
    }
};

const runOnGaLoad = (iteration = 0) => {
    const gaCookie = getCookie('_ga');
    const cookieExist = !!gaCookie;

    if (cookieExist) {
        const requestOptions = {
            request: {
                cache: 'no-cache'
            }
        };

        get('/extend-cookie', {}, requestOptions).then(() => {
            saveSession(COOKIE_EXTENSION_SESSION, Date.now());
        });
    } else if (iteration < 10) {
        setTimeout(() => {
            runOnGaLoad(iteration + 1);
        }, 500);
    }
};

/**
 * Extend _ga cookie expiration date on Safari (ITP prevention).
 * Request is handled in CloudFlare Workers.
 */
export const extendGaCookieExpirationDate = () => {
    const session = loadSession(COOKIE_EXTENSION_SESSION);
    let timeDifference = COOKIE_EXTENSION_INTERVAL + 1;

    if (typeof session !== 'undefined') {
        timeDifference = getDaysFromMilliseconds((Date.now() - session));
    }

    if (timeDifference > COOKIE_EXTENSION_INTERVAL) {
        runOnGaLoad();
    }
};
